import { useEffect } from 'react'
import {
  Button,
  DatePicker,
  Drawer,
  Input,
  Select,
  Switch,
  useDrawer,
} from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Mail } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Can } from '@/common/components/Can'
import { Gender } from '@/common/models/person'
import { UserType } from '@/common/models/user'
import { getLocale } from '@/common/utils/getLocale'
import { translateGender } from '@/common/utils/translateGender'
import { translateUserType } from '@/common/utils/translateUserType'
import { useInactivateUser } from '../hooks/useInactivateUser'
import { userPaginatedDTO } from '../hooks/usePaginateUsers'
import { useSelectedUserStore } from '../stores/useSelectedUserStore'

export function DrawerViewUser() {
  const { t } = useTranslation(['registers', 'common'])
  const { close } = useDrawer('drawer-view-user')
  const hookForm = useForm()
  const { user, setUser } = useSelectedUserStore()
  const { handleInactivateUser } = useInactivateUser()

  const handleClose = () => {
    close()
    setUser(null)
    hookForm.reset()
  }

  useEffect(() => {
    if (user) {
      hookForm.reset({
        email: user.email,
        birthDate: user.Person.birthDate
          ? new Date(user.Person.birthDate)
          : undefined,
        displayName: user.UserPreference.displayName,
        documentNumber: user.Person.documentNumber || '',
        genre: user.Person.genre,
        isForeign: user.Person.isForeign,
        name: user.Person.name,
        userType: user.userType,
        editAllowedPosts: user.UserPreference.allowedPosts,
      })
    }
  }, [user, hookForm])

  const IS_FOREIGN = hookForm.watch('isForeign')

  const USER_TYPE_OPTIONS = Object.values(UserType).map((type) => ({
    label: translateUserType(t, type),
    value: type,
  }))

  const GENDER_OPTIONS = Object.values(Gender).map((gender) => ({
    label: translateGender(t, gender),
    value: gender,
  }))

  return (
    <Drawer.Root id="drawer-view-user">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('registers:users.drawer_view_user.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('registers:users.drawer_view_user.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form className="space-y-6">
            <Input.Icon
              disabled
              icon={<Mail />}
              name="email"
              label={t('registers:users.drawer_view_user.fields.email.label')}
            />

            <Select.Single
              disabled
              name="userType"
              label={t(
                'registers:users.drawer_view_user.fields.user_type.label',
              )}
              options={USER_TYPE_OPTIONS}
            />

            <h2 className="text-2xl font-semibold text-brand-700">
              {t('registers:users.drawer_view_user.body.subtitle')}
            </h2>

            <div className="flex justify-between gap-4">
              <div className="space-y-2 flex-1">
                {!IS_FOREIGN && (
                  <Input.Mask
                    disabled
                    name="documentNumber"
                    mask="###.###.###-##"
                    label={t(
                      'registers:users.drawer_view_user.fields.cpf.label',
                    )}
                  />
                )}

                {IS_FOREIGN && (
                  <Input.Base
                    disabled
                    name="documentNumber"
                    label={t(
                      'registers:users.drawer_view_user.fields.document_number.label',
                    )}
                  />
                )}
              </div>

              <div className="flex-1">
                <Input.Base
                  disabled
                  name="displayName"
                  label={t(
                    'registers:users.drawer_view_user.fields.display_name.label',
                  )}
                />
              </div>
            </div>

            <Input.Base
              disabled
              name="name"
              label={t('registers:users.drawer_view_user.fields.name.label')}
            />

            <div className="flex justify-between gap-4">
              <DatePicker.Single
                disabled
                locale={getLocale()}
                name="birthDate"
                label={t(
                  'registers:users.drawer_view_user.fields.birth_date.label',
                )}
              />

              <Select.Single
                disabled
                name="genre"
                label={t(
                  'registers:users.drawer_view_user.fields.gender.label',
                )}
                options={GENDER_OPTIONS}
              />
            </div>

            <Switch.Root className="flex justify-between">
              <Switch.Label htmlFor="editAllowedPosts">
                {t(
                  'registers:users.drawer_view_user.fields.allowed_posts.label',
                )}
              </Switch.Label>

              <Switch.Trigger
                id="editAllowedPosts"
                name="editAllowedPosts"
                disabled
              />
            </Switch.Root>
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer className="flex items-center justify-end gap-4">
        {user && (
          <Can I="inactivate" this={userPaginatedDTO.parse(user)}>
            <Button.Root
              variant="secondary"
              color="danger"
              onClick={() => handleInactivateUser(user.id)}
              className="mr-auto"
            >
              {t('registers:users.drawer_view_user.footer.submit_button.label')}
            </Button.Root>
          </Can>
        )}

        <Button.Root variant="secondary" color="brand" onClick={handleClose}>
          {t('registers:users.drawer_view_user.footer.close_button.label')}
        </Button.Root>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
